import type { Contact, ContactPatch, ExtraValue, SchemaOptions, WritableContactField } from './types.js';
import type {
  NotionDataSource,
  NotionPage,
  NotionPropertyValue,
  NotionWriteProperties,
  RichTextItem,
} from './notion-types.js';

/**
 * Canonical mapping for the Contacts data source (§4.2, D-4).
 * `notion` is the property name as Notion shows it, trimmed. Notion's real name of
 * the title is " Company Name" (leading space); we match on the trimmed form and
 * resolve the exact name from the live schema when writing.
 */
export type NotionFieldType =
  | 'title'
  | 'rich_text'
  | 'select'
  | 'status'
  | 'multi_select'
  | 'people'
  | 'date'
  | 'email'
  | 'phone_number'
  | 'url'
  | 'created_time'
  | 'last_edited_time'
  | 'last_edited_by';

export interface FieldMapping {
  key: keyof Contact;
  notion: string;
  type: NotionFieldType;
  writable: boolean;
}

export const CONTACT_FIELDS: readonly FieldMapping[] = [
  { key: 'companyName', notion: 'Company Name', type: 'title', writable: true },
  { key: 'contact', notion: 'Contact', type: 'rich_text', writable: true },
  { key: 'contactRole', notion: 'Contact Role', type: 'select', writable: true },
  { key: 'email', notion: 'Email', type: 'email', writable: true },
  { key: 'phone', notion: 'Phone Number', type: 'phone_number', writable: true },
  { key: 'linkedin', notion: 'LinkedIn Profile', type: 'url', writable: true },
  { key: 'companySite', notion: 'Company Site', type: 'url', writable: true },
  { key: 'stage', notion: 'Stage', type: 'select', writable: true },
  { key: 'status', notion: 'Status', type: 'status', writable: true },
  { key: 'nextAction', notion: 'Next Action', type: 'multi_select', writable: true },
  { key: 'owner', notion: 'Owner', type: 'people', writable: true },
  { key: 'nextContactDate', notion: 'Next Contact Date', type: 'date', writable: true },
  { key: 'lastContactDate', notion: 'Last Contact Date', type: 'date', writable: true },
  { key: 'leadSource', notion: 'Lead Source', type: 'select', writable: true },
  { key: 'industry', notion: 'Industry', type: 'select', writable: true },
  { key: 'description', notion: 'Decription', type: 'rich_text', writable: false },
  { key: 'createdTime', notion: 'Created time', type: 'created_time', writable: false },
  { key: 'lastEditedTime', notion: 'Last edited time', type: 'last_edited_time', writable: false },
  { key: 'lastEditedBy', notion: 'Last edited by', type: 'last_edited_by', writable: false },
];

const BY_KEY = new Map(CONTACT_FIELDS.map((f) => [f.key, f] as const));
const BY_NOTION = new Map(CONTACT_FIELDS.map((f) => [f.notion, f] as const));

export function fieldByKey(key: keyof Contact): FieldMapping | undefined {
  return BY_KEY.get(key);
}

/** Notion property name (trimmed) for an app field, e.g. `stage` → `Stage`. */
export function notionName(key: WritableContactField): string {
  const f = BY_KEY.get(key);
  if (!f) throw new Error(`Unknown field ${key}`);
  return f.notion;
}

export function plainText(items: RichTextItem[] | undefined): string {
  if (!items) return '';
  return items.map((r) => r.plain_text ?? r.text?.content ?? '').join('');
}

export function richText(text: string): RichTextItem[] {
  if (!text) return [];
  // Notion limits a single text item to 2000 chars.
  const chunks: RichTextItem[] = [];
  for (let i = 0; i < text.length; i += 2000) {
    chunks.push({ type: 'text', text: { content: text.slice(i, i + 2000) } });
  }
  return chunks;
}

/** Date-only part of a Notion date start (we never write times on Contacts). */
export function dateOnly(start: string | null | undefined): string | null {
  if (!start) return null;
  return start.slice(0, 10);
}

/** Reduce any Notion property value to a plain JSON value for `extra` (§4.2). */
export function toExtraValue(p: NotionPropertyValue): ExtraValue {
  switch (p.type) {
    case 'title':
      return plainText(p.title);
    case 'rich_text':
      return plainText(p.rich_text);
    case 'select':
      return p.select?.name ?? null;
    case 'status':
      return p.status?.name ?? null;
    case 'multi_select':
      return p.multi_select.map((o) => o.name);
    case 'date':
      return p.date?.start ?? null;
    case 'people':
      return p.people.map((u) => u.id);
    case 'email':
      return p.email;
    case 'phone_number':
      return p.phone_number;
    case 'url':
      return p.url;
    case 'number':
      return p.number;
    case 'checkbox':
      return p.checkbox;
    case 'created_time':
      return p.created_time;
    case 'last_edited_time':
      return p.last_edited_time;
    case 'created_by':
      return p.created_by.id;
    case 'last_edited_by':
      return p.last_edited_by.id;
    case 'relation':
      return p.relation.map((r) => r.id);
    case 'files':
      return p.files.map((f) => f.name);
    case 'formula': {
      const f = p.formula;
      if (f.type === 'string') return f.string ?? null;
      if (f.type === 'number') return f.number ?? null;
      if (f.type === 'boolean') return f.boolean ?? null;
      if (f.type === 'date') return f.date?.start ?? null;
      return null;
    }
    case 'rollup':
      return p.rollup.type === 'number' ? (p.rollup.number ?? null) : null;
    case 'unique_id':
      return p.unique_id.prefix ? `${p.unique_id.prefix}-${p.unique_id.number}` : p.unique_id.number;
    default:
      // A type this build does not know (UnknownPropertyValue at runtime).
      return null;
  }
}

/** Notion page → canonical Contact. Unknown properties land in `extra`. */
export function contactFromPage(page: NotionPage): Contact {
  const props = new Map<string, NotionPropertyValue>();
  for (const [name, value] of Object.entries(page.properties)) props.set(name.trim(), value);

  const get = (notion: string): NotionPropertyValue | undefined => props.get(notion);
  const text = (n: string) => {
    const p = get(n);
    if (!p) return '';
    return p.type === 'title' ? plainText(p.title) : p.type === 'rich_text' ? plainText(p.rich_text) : '';
  };
  const select = (n: string) => {
    const p = get(n);
    if (!p) return null;
    if (p.type === 'select') return p.select?.name ?? null;
    if (p.type === 'status') return p.status?.name ?? null;
    return null;
  };
  const str = (n: string): string | null => {
    const p = get(n);
    if (!p) return null;
    if (p.type === 'email') return p.email;
    if (p.type === 'phone_number') return p.phone_number;
    if (p.type === 'url') return p.url;
    return null;
  };
  const date = (n: string) => {
    const p = get(n);
    return p && p.type === 'date' ? dateOnly(p.date?.start) : null;
  };
  const multi = (n: string): string[] => {
    const p = get(n);
    return p && p.type === 'multi_select' ? p.multi_select.map((o) => o.name) : [];
  };
  const people = (n: string): string[] => {
    const p = get(n);
    return p && p.type === 'people' ? p.people.map((u) => u.id) : [];
  };

  const nextActions = multi('Next Action');
  const lastEditedByProp = get('Last edited by');

  const extra: Record<string, ExtraValue> = {};
  for (const [name, value] of props) {
    if (BY_NOTION.has(name)) continue;
    // A human may turn the CRM Log relation two-way in Notion (D-18); the back-relation
    // changes on every log entry and must not count as an edit of the contact.
    if (name === 'CRM Log' && value.type === 'relation') continue;
    extra[name] = toExtraValue(value);
  }

  return {
    id: page.id,
    companyName: text('Company Name'),
    contact: text('Contact'),
    contactRole: select('Contact Role'),
    email: str('Email'),
    phone: str('Phone Number'),
    linkedin: str('LinkedIn Profile'),
    companySite: str('Company Site'),
    stage: select('Stage'),
    status: select('Status'),
    nextAction: nextActions[0] ?? null,
    nextActionExtra: nextActions.slice(1),
    owner: people('Owner'),
    nextContactDate: date('Next Contact Date'),
    lastContactDate: date('Last Contact Date'),
    leadSource: select('Lead Source'),
    industry: select('Industry'),
    description: text('Decription'),
    createdTime: page.created_time,
    lastEditedTime: page.last_edited_time,
    lastEditedBy:
      lastEditedByProp && lastEditedByProp.type === 'last_edited_by'
        ? lastEditedByProp.last_edited_by.id
        : (page.last_edited_by?.id ?? null),
    url: page.url,
    archived: Boolean(page.archived || page.in_trash),
    extra,
  };
}

/**
 * Exact Notion property names keyed by trimmed name, from the live data source.
 * Needed because Notion's title is literally " Company Name".
 */
export function exactNames(ds: NotionDataSource): Map<string, string> {
  const m = new Map<string, string>();
  for (const name of Object.keys(ds.properties)) m.set(name.trim(), name);
  return m;
}

/**
 * Canonical patch → Notion `properties` for pages.update / pages.create.
 * `names` resolves trimmed names to Notion's exact names (see exactNames).
 * Only writable fields are accepted; anything else throws, so a bug can never
 * touch a column the app does not own (D-4).
 */
export function propertiesFromPatch(patch: ContactPatch, names?: Map<string, string>): NotionWriteProperties {
  const out: NotionWriteProperties = {};
  const nameOf = (trimmed: string) => names?.get(trimmed) ?? trimmed;
  for (const [key, value] of Object.entries(patch) as [WritableContactField, unknown][]) {
    const f = BY_KEY.get(key);
    if (!f || !f.writable) throw new Error(`Field ${key} is not writable`);
    const n = nameOf(f.notion);
    switch (f.type) {
      case 'title':
        out[n] = { title: richText(String(value ?? '')) };
        break;
      case 'rich_text':
        out[n] = { rich_text: richText(String(value ?? '')) };
        break;
      case 'select':
        out[n] = { select: value ? { name: String(value) } : null };
        break;
      case 'status':
        out[n] = { status: value ? { name: String(value) } : null };
        break;
      case 'multi_select':
        // D-12: the app writes exactly one value (or none).
        out[n] = { multi_select: value ? [{ name: String(value) }] : [] };
        break;
      case 'people':
        out[n] = { people: (Array.isArray(value) ? value : []).map((id) => ({ object: 'user', id: String(id) })) };
        break;
      case 'date':
        out[n] = { date: value ? { start: String(value).slice(0, 10) } : null };
        break;
      case 'email':
        out[n] = { email: value ? String(value) : null };
        break;
      case 'phone_number':
        out[n] = { phone_number: value ? String(value) : null };
        break;
      case 'url':
        out[n] = { url: value ? String(value) : null };
        break;
      default:
        throw new Error(`Field ${key} has read-only type ${f.type}`);
    }
  }
  return out;
}

/** Option lists per trimmed property name, from the data source object (§4.2). */
export function optionsFromDataSource(ds: NotionDataSource): SchemaOptions {
  const out: SchemaOptions = {};
  for (const [name, p] of Object.entries(ds.properties)) {
    const opts = p.select?.options ?? p.multi_select?.options ?? p.status?.options;
    if (!opts) continue;
    out[name.trim()] = opts.map((o) => ({ name: o.name, color: o.color ?? 'default' }));
  }
  return out;
}

/** Properties the sync treats as "edited" when they differ (everything but the volatile ones). */
export function contactsDiffer(a: Contact, b: Contact): boolean {
  return JSON.stringify(stripVolatile(a)) !== JSON.stringify(stripVolatile(b));
}

function stripVolatile(c: Contact) {
  const { lastEditedTime: _t, lastEditedBy: _b, ...rest } = c;
  return rest;
}
